/**
 * The render-settings panel — dev chrome, not game chrome. It is reached from
 * the Escape menu, every control applies on input, and nothing here touches the
 * simulation: these knobs move draw distances, budgets and buffer sizes, which
 * is law-9-safe by construction (observation writes nothing, and neither does
 * choosing how much of the world to draw).
 *
 * **It used to own Tab**, which is one of the two keys a player presses without
 * thinking, and it was spending it on draw distance. Tab is Become now — *show
 * me what kind of god I am becoming* — and the render knobs moved to where a
 * player actually looks for settings. They are useful and they are kept; they
 * simply do not get a primary key.
 *
 * The panel owns the numbers and hands them out; main.ts owns every effect.
 * Multipliers the frame loop reads live (view radius, far forest) simply sit
 * on the returned object; the ones with a side effect to run (pixel ratio,
 * fog, shadow map, LOD scale, litter scale) go through `onChange`, which
 * main implements as one idempotent apply().
 *
 * Persisted in localStorage under its own key — deliberately outside the
 * `terra-` save namespace's version gate, because a machine's comfortable
 * draw distance survives every snapshot version bump.
 */

export interface RenderSettings {
  /** × on the streamed ring's view radius (the `vr` curve in main). */
  viewMul: number;
  /** × on the LOD rungs' distances (ChunkManager.detailScale). */
  detailMul: number;
  /** × on the far canopy's drawn share. */
  canopyMul: number;
  /** × on fog near/far — how far the air lets you see. */
  fogMul: number;
  /** × on the litter disc, landmark band and litter budget. */
  reachMul: number;
  /** × on the grass carpet's radius, range and blade budget. */
  grassMul: number;
  /** × on the renderer's pixel ratio (against the device's own, capped 2). */
  resMul: number;
  /** Shadow map side in texels; 0 disables shadows entirely. */
  shadowRes: number;
}

export const RENDER_DEFAULTS: RenderSettings = {
  viewMul: 1, detailMul: 1, canopyMul: 1, fogMul: 1,
  reachMul: 1, grassMul: 1, resMul: 1, shadowRes: 2048,
};

/** What the footer reads twice a second while the panel is open. */
export interface RenderStats {
  fps: number;
  /** Draw calls and triangles of the last rendered frame. */
  calls: number;
  tris: number;
  chunks: number;
}

const STORE_KEY = 'terra-render-settings';

const SLIDERS: Array<{
  key: Exclude<keyof RenderSettings, 'shadowRes'>;
  label: string; min: number; max: number; step: number;
}> = [
  { key: 'viewMul',   label: 'view distance', min: 0.3,  max: 2.5, step: 0.05 },
  { key: 'detailMul', label: 'lod rings',     min: 0.3,  max: 2.5, step: 0.05 },
  { key: 'canopyMul', label: 'far forest',    min: 0,    max: 1.5, step: 0.05 },
  { key: 'fogMul',    label: 'haze distance', min: 0.4,  max: 3,   step: 0.05 },
  { key: 'reachMul',  label: 'litter reach',  min: 0,    max: 2,   step: 0.05 },
  { key: 'grassMul',  label: 'grass',         min: 0,    max: 2,   step: 0.05 },
  { key: 'resMul',    label: 'resolution',    min: 0.25, max: 2,   step: 0.05 },
];

const SHADOW_CHOICES = [0, 1024, 2048, 4096];

const load = (): RenderSettings => {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return { ...RENDER_DEFAULTS };
    const p = JSON.parse(raw) as Partial<RenderSettings>;
    const s = { ...RENDER_DEFAULTS };
    for (const d of SLIDERS) {
      const v = p[d.key];
      if (typeof v === 'number' && isFinite(v)) s[d.key] = Math.min(d.max, Math.max(d.min, v));
    }
    if (typeof p.shadowRes === 'number' && SHADOW_CHOICES.includes(p.shadowRes))
      s.shadowRes = p.shadowRes;
    return s;
  } catch { return { ...RENDER_DEFAULTS }; }
};

/** Opening and closing it. No key of its own — main.ts owns every key. */
export interface SettingsPanel {
  readonly isOpen: boolean;
  open(): void;
  close(): void;
  toggle(): void;
}

/**
 * Build the panel and return the LIVE settings object — mutated in place, so
 * the frame loop can read multipliers off it with no calls — alongside the
 * handle that opens it. `onChange` fires after every mutation, including once
 * at creation when stored settings differ from the defaults.
 *
 * It binds no keys. It used to bind Tab itself, which is exactly the pattern
 * that made Escape ambiguous — every panel racing for the key it happens to
 * want. One key, one owner, and the owner is `main.ts`.
 */
export function createSettings(
  onChange: (s: RenderSettings) => void,
  stats: () => RenderStats,
): { values: RenderSettings; panel: SettingsPanel } {
  const s = load();

  const style = document.createElement('style');
  // Dev chrome, and it should look like it — but it still has to be READABLE
  // over a bright sky, which is what `tools/chrome.ts` measured the rest of the
  // panels against. Same pane, same edge, same two inks; the only thing that
  // marks it as scaffolding rather than game chrome is that it says nothing in
  // the world's voice.
  style.textContent = `
    /* Right-hand margin, not the left: the left is the selection card's now,
       and a settings sheet must not sit on top of what the player chose. It
       covers the Chronicle instead, which is the surface you are least likely
       to be reading while dragging a draw-distance slider. */
    #rsettings { position:fixed; right:18px; top:16px; width:252px; z-index:40;
      background:var(--pane); border:1px solid var(--edge); border-radius:10px;
      backdrop-filter:blur(7px); -webkit-backdrop-filter:blur(7px);
      box-shadow:var(--lift), inset 0 1px 0 rgba(233,227,210,.05);
      padding:12px 14px 10px; display:none;
      font-size:10px; color:var(--dim); }
    #rsettings.open { display:block; }
    #rsettings header { display:flex; justify-content:space-between;
      letter-spacing:.24em; text-transform:uppercase; color:var(--ink);
      font-size:9px; margin-bottom:9px; }
    #rsettings header em { font-style:normal; color:var(--dim); }
    #rsettings .row { display:grid; grid-template-columns:88px 1fr 38px;
      align-items:center; gap:8px; margin:5px 0; }
    #rsettings .row .v { text-align:right; color:var(--ink); font-variant-numeric:tabular-nums; }
    #rsettings input[type=range] { -webkit-appearance:none; appearance:none;
      width:100%; height:2px; background:var(--line); border-radius:1px; }
    #rsettings input[type=range]::-webkit-slider-thumb { -webkit-appearance:none;
      appearance:none; width:9px; height:9px; border-radius:50%;
      background:var(--gold); cursor:pointer; }
    #rsettings .shadows { display:flex; gap:2px; }
    #rsettings .shadows button { padding:2px 7px; font-size:9px;
      letter-spacing:.08em; color:var(--dim); border-radius:8px; }
    #rsettings .shadows button.on { color:var(--gold); background:rgba(217,183,90,.13); }
    #rsettings footer { margin-top:9px; padding-top:8px; border-top:1px solid var(--line);
      display:flex; justify-content:space-between; align-items:baseline; }
    #rsettings footer .stats { font-variant-numeric:tabular-nums; line-height:1.6; }
    #rsettings footer button { color:var(--dim); font-size:9px;
      letter-spacing:.1em; text-transform:uppercase; }
    #rsettings footer button:hover { color:var(--ink); }
  `;
  document.head.appendChild(style);

  const el = document.createElement('div');
  el.id = 'rsettings';
  el.innerHTML = `<header><span>render</span><em>esc closes</em></header>`
    + SLIDERS.map((d) => `
      <div class="row" data-k="${d.key}">
        <span>${d.label}</span>
        <input type="range" min="${d.min}" max="${d.max}" step="${d.step}" value="${s[d.key]}">
        <span class="v"></span>
      </div>`).join('')
    + `<div class="row"><span>shadows</span><span class="shadows">${
        SHADOW_CHOICES.map((r) => `<button data-r="${r}">${r === 0 ? 'off' : r / 1024 + 'k'}</button>`).join('')
      }</span><span class="v"></span></div>`
    + `<footer><span class="stats"></span><button data-reset>reset</button></footer>`;
  document.body.appendChild(el);

  const statsEl = el.querySelector<HTMLElement>('.stats')!;
  const shadowBtns = [...el.querySelectorAll<HTMLButtonElement>('.shadows button')];

  const save = () => { try { localStorage.setItem(STORE_KEY, JSON.stringify(s)); } catch { /* full/blocked: the session still works */ } };

  const paint = () => {
    for (const d of SLIDERS) {
      const row = el.querySelector<HTMLElement>(`.row[data-k="${d.key}"]`)!;
      row.querySelector<HTMLInputElement>('input')!.value = String(s[d.key]);
      row.querySelector<HTMLElement>('.v')!.textContent = `×${s[d.key].toFixed(2)}`;
    }
    for (const b of shadowBtns) b.classList.toggle('on', Number(b.dataset.r) === s.shadowRes);
  };

  for (const d of SLIDERS) {
    const input = el.querySelector<HTMLInputElement>(`.row[data-k="${d.key}"] input`)!;
    input.addEventListener('input', () => {
      s[d.key] = Number(input.value);
      paint(); save(); onChange(s);
    });
  }
  for (const b of shadowBtns) {
    b.addEventListener('click', () => {
      s.shadowRes = Number(b.dataset.r);
      paint(); save(); onChange(s);
    });
  }
  el.querySelector<HTMLButtonElement>('[data-reset]')!.addEventListener('click', () => {
    Object.assign(s, RENDER_DEFAULTS);
    paint(); save(); onChange(s);
  });

  // the panel floats over the stage; a click on it must not hand the mouse
  // to the pointer-lock flow underneath
  el.addEventListener('pointerdown', (e) => e.stopPropagation());

  let statsTimer = 0;
  const renderStats = () => {
    const st = stats();
    const tris = st.tris >= 1e6 ? `${(st.tris / 1e6).toFixed(1)} M` : `${Math.round(st.tris / 1e3)} k`;
    statsEl.innerHTML = `${Math.round(st.fps)} fps · ${(1000 / Math.max(1, st.fps)).toFixed(1)} ms<br>` +
      `${st.calls} calls · ${tris} tris · ${st.chunks} chunks`;
  };
  const setOpen = (open: boolean) => {
    el.classList.toggle('open', open);
    if (open) {
      renderStats();
      statsTimer = window.setInterval(renderStats, 500);
    } else if (statsTimer) {
      clearInterval(statsTimer);
      statsTimer = 0;
    }
  };

  paint();
  // a stored profile must take effect at boot, not on first touch
  onChange(s);
  return {
    values: s,
    panel: {
      get isOpen() { return el.classList.contains('open'); },
      open: () => setOpen(true),
      close: () => setOpen(false),
      toggle: () => setOpen(!el.classList.contains('open')),
    },
  };
}
